import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DollarSign, Users, Heart, TrendingUp, FileText, Radio, Crown, Send, Zap, Copy, Share2, Film, Clock, Timer, Image, Upload, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { creatorAPI, videoAPI, mediaAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function CreatorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileForm, setProfileForm] = useState({ bio: '', display_name: '', tags: '', tier_standard_price: 10, tier_vip_price: 25, tier_diamond_price: 50, referral_bonus_creator: 5, referral_bonus_subscriber: 2 });
  const [savingProfile, setSavingProfile] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '', is_ppv: false, ppv_price: 5, tier_required: 'free', teaser_prompt: '', is_expiring: false, expires_hours: 24 });
  const [posting, setPosting] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);  // { file, preview, uploading, path }
  const [uploading, setUploading] = useState(false);

  // PPV Message
  const [ppvMsg, setPpvMsg] = useState({ recipient_id: '', preview_text: '', content: '', price: 5 });
  const [sendingPPV, setSendingPPV] = useState(false);

  // Referral
  const [referral, setReferral] = useState(null);
  const [loadingRef, setLoadingRef] = useState(false);

  const appUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsRes, profileRes] = await Promise.all([
          creatorAPI.analytics(),
          creatorAPI.getProfile(user.user_id || user.id),
        ]);
        setAnalytics(analyticsRes.data);
        const p = profileRes.data;
        setProfile(p);
        setProfileForm({
          bio: p.bio || '',
          display_name: p.display_name || user.name || '',
          tags: (p.tags || []).join(', '),
          tier_standard_price: p.tiers?.standard?.price || 10,
          tier_vip_price: p.tiers?.vip?.price || 25,
          tier_diamond_price: p.tiers?.diamond?.price || 50,
          referral_bonus_creator: p.referral_bonus_creator || 5,
          referral_bonus_subscriber: p.referral_bonus_subscriber || 2,
        });
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    if (user) fetchData();
  }, [user]);

  const loadReferral = async () => {
    if (referral) return;
    setLoadingRef(true);
    try {
      const res = await creatorAPI.getReferral();
      setReferral(res.data);
    } catch (e) { toast.error('Failed to load referral data'); }
    finally { setLoadingRef(false); }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await creatorAPI.updateProfile({
        ...profileForm,
        tags: profileForm.tags.split(',').map(t => t.trim()).filter(Boolean),
      });
      toast.success('Profile saved!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSavingProfile(false); }
  };

  const handleCreatePost = async () => {
    if (!newPost.content.trim()) { toast.error('Content required'); return; }

    // Upload any pending media files first
    setPosting(true);
    let mediaPaths = [];
    if (mediaFiles.length > 0) {
      setUploading(true);
      for (const mf of mediaFiles) {
        if (mf.path) { mediaPaths.push(mf.path); continue; }
        try {
          const res = await mediaAPI.upload(mf.file);
          mediaPaths.push(res.data.storage_path);
        } catch (e) { toast.error(`Failed to upload ${mf.file.name}`); }
      }
      setUploading(false);
    }

    try {
      await creatorAPI.createPost({ ...newPost, media_paths: mediaPaths });
      const hasTeaser = !!newPost.teaser_prompt.trim();
      toast.success(hasTeaser ? 'Post published! Teaser video generating...' : 'Post published!');
      setNewPost({ title: '', content: '', is_ppv: false, ppv_price: 5, tier_required: 'free', teaser_prompt: '', is_expiring: false, expires_hours: 24 });
      setMediaFiles([]);
      const analyticsRes = await creatorAPI.analytics();
      setAnalytics(analyticsRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to post'); }
    finally { setPosting(false); }
  };

  const handleMediaSelect = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.slice(0, 10 - mediaFiles.length).map(f => ({
      file: f,
      preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
      path: null,
    }));
    setMediaFiles([...mediaFiles, ...newFiles]);
  };

  const removeMedia = (idx) => {
    const updated = [...mediaFiles];
    if (updated[idx].preview) URL.revokeObjectURL(updated[idx].preview);
    updated.splice(idx, 1);
    setMediaFiles(updated);
  };

  const handleSendPPV = async () => {
    if (!ppvMsg.recipient_id || !ppvMsg.content) { toast.error('Recipient and content required'); return; }
    setSendingPPV(true);
    try {
      await creatorAPI.sendPPV(ppvMsg);
      toast.success('PPV message sent!');
      setPpvMsg({ recipient_id: '', preview_text: '', content: '', price: 5 });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); }
    finally { setSendingPPV(false); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard!')).catch(() => toast.error('Failed to copy'));
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-950"><div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!analytics) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><p className="text-zinc-400">Failed to load dashboard</p></div>;

  const stats = [
    { label: 'Total Earnings', value: formatCurrency(analytics.total_earnings), icon: DollarSign, color: 'text-green-400', bg: 'bg-green-500/10' },
    { label: 'Subscribers', value: analytics.subscriber_count, icon: Users, color: 'text-rose-400', bg: 'bg-rose-500/10' },
    { label: 'Posts', value: analytics.total_posts, icon: FileText, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { label: 'Total Likes', value: analytics.total_likes, icon: Heart, color: 'text-pink-400', bg: 'bg-pink-500/10' },
  ];

  return (
    <div className="min-h-screen bg-zinc-950 pb-12" data-testid="creator-dashboard-page">
      {/* Header */}
      <section className="bg-gradient-to-b from-rose-950/30 to-zinc-950 border-b border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-bold text-2xl text-white" data-testid="creator-dashboard-title">Creator Dashboard</h1>
              <p className="text-zinc-400 text-sm mt-1">Manage your content, track earnings, and grow your audience</p>
            </div>
            <Link to={`/creator/profile/${user?.user_id || user?.id}`}>
              <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="view-profile-btn">
                View Public Profile
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          {stats.map((stat, idx) => (
            <motion.div key={stat.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
              <div className="bg-zinc-900 border border-zinc-800 p-4" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 ${stat.bg} flex items-center justify-center`}>
                    <stat.icon size={16} className={stat.color} />
                  </div>
                  <span className="text-xs text-zinc-500">{stat.label}</span>
                </div>
                <p className="font-bold text-2xl text-white">{stat.value}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Revenue Breakdown */}
        <div className="bg-zinc-900 border border-zinc-800 p-5 mb-8">
          <h3 className="font-bold text-white mb-4 flex items-center gap-2"><TrendingUp size={18} className="text-green-400" /> Revenue Breakdown</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-xs text-zinc-500 mb-1">Subscriptions (80%)</p>
              <p className="font-bold text-xl text-white">{formatCurrency(analytics.subscription_earnings)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-zinc-500 mb-1">Tips (90%)</p>
              <p className="font-bold text-xl text-white">{formatCurrency(analytics.tip_earnings)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-zinc-500 mb-1">PPV (85%)</p>
              <p className="font-bold text-xl text-white">{formatCurrency(analytics.ppv_earnings)}</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-zinc-800">
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-400">PPV Unlocks</span>
              <span className="text-white font-bold">{analytics.total_ppv_unlocks}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="post" className="w-full">
          <TabsList className="bg-zinc-900 border border-zinc-800 p-1 mb-4 flex-wrap">
            <TabsTrigger value="post" className="data-[state=active]:bg-rose-500 data-[state=active]:text-white text-sm text-zinc-400">New Post</TabsTrigger>
            <TabsTrigger value="referral" onClick={loadReferral} className="data-[state=active]:bg-rose-500 data-[state=active]:text-white text-sm text-zinc-400">Referrals</TabsTrigger>
            <TabsTrigger value="profile" className="data-[state=active]:bg-rose-500 data-[state=active]:text-white text-sm text-zinc-400">Edit Profile</TabsTrigger>
            <TabsTrigger value="ppv" className="data-[state=active]:bg-rose-500 data-[state=active]:text-white text-sm text-zinc-400">PPV Message</TabsTrigger>
            <TabsTrigger value="rooms" className="data-[state=active]:bg-rose-500 data-[state=active]:text-white text-sm text-zinc-400">My Rooms</TabsTrigger>
          </TabsList>

          {/* New Post Tab */}
          <TabsContent value="post">
            <div className="bg-zinc-900 border border-zinc-800 p-6">
              <h3 className="font-bold text-white mb-4">Create Post</h3>
              <div className="space-y-3">
                <Input placeholder="Title (optional)" value={newPost.title} onChange={e => setNewPost({ ...newPost, title: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" data-testid="dashboard-post-title" />
                <Textarea placeholder="Write your content..." value={newPost.content} onChange={e => setNewPost({ ...newPost, content: e.target.value })} rows={4} className="bg-zinc-800 border-zinc-700 text-white" data-testid="dashboard-post-content" />

                {/* Media Upload */}
                <div className="bg-zinc-800/50 border border-zinc-700 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Image size={16} className="text-blue-400" />
                      <span className="text-sm font-semibold text-white">Media Attachments</span>
                      <span className="text-[10px] text-zinc-500 bg-zinc-700 px-2 py-0.5">{mediaFiles.length}/10</span>
                    </div>
                    <label className="cursor-pointer">
                      <input type="file" multiple accept="image/*,video/mp4,video/webm" onChange={handleMediaSelect} className="hidden" data-testid="media-upload-input" />
                      <span className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors">
                        <Upload size={12} /> Add Files
                      </span>
                    </label>
                  </div>
                  {mediaFiles.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {mediaFiles.map((mf, idx) => (
                        <div key={idx} className="relative group w-20 h-20 bg-zinc-900 border border-zinc-700 overflow-hidden">
                          {mf.preview ? (
                            <img src={mf.preview} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="flex items-center justify-center h-full text-xs text-zinc-500"><Film size={20} /></div>
                          )}
                          <button onClick={() => removeMedia(idx)} className="absolute top-0 right-0 bg-red-500 text-white p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-media-${idx}`}>
                            <X size={12} />
                          </button>
                          <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-[8px] text-zinc-300 text-center truncate px-1">{mf.file.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {mediaFiles.length === 0 && <p className="text-xs text-zinc-500">Upload images or short videos to attach to your post.</p>}
                </div>

                {/* Teaser Video Section */}
                <div className="bg-zinc-800/50 border border-zinc-700 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Film size={16} className="text-violet-400" />
                    <span className="text-sm font-semibold text-white">Teaser Video (Free Preview)</span>
                    <span className="text-[10px] text-zinc-500 bg-zinc-700 px-2 py-0.5">Sora 2</span>
                  </div>
                  <p className="text-xs text-zinc-400 mb-2">Describe a short cinematic teaser clip. This video is FREE for everyone to see — it's your hook to drive PPV/subscription sales.</p>
                  <Textarea
                    placeholder="e.g., Mysterious silhouette walking through neon-lit rain, dramatic slow motion..."
                    value={newPost.teaser_prompt}
                    onChange={e => setNewPost({ ...newPost, teaser_prompt: e.target.value })}
                    rows={2}
                    className="bg-zinc-900 border-zinc-700 text-white text-sm"
                    data-testid="teaser-prompt-input"
                  />
                  {newPost.teaser_prompt && <p className="text-[10px] text-violet-400 mt-1">An 8-second teaser will be generated via Sora 2 (takes 2-5 min)</p>}
                </div>

                {/* Expiry Toggle */}
                <div className="flex items-center gap-4 bg-zinc-800/50 border border-zinc-700 p-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newPost.is_expiring}
                      onCheckedChange={v => setNewPost({ ...newPost, is_expiring: v })}
                      data-testid="expiry-toggle"
                    />
                    <Label className="text-sm text-zinc-300 cursor-pointer flex items-center gap-1">
                      <Timer size={14} className="text-amber-400" /> Expiring Post
                    </Label>
                  </div>
                  {newPost.is_expiring && (
                    <div className="flex items-center gap-2">
                      <select
                        value={newPost.expires_hours}
                        onChange={e => setNewPost({ ...newPost, expires_hours: parseInt(e.target.value) })}
                        className="bg-zinc-900 border border-zinc-700 text-white text-sm px-2 py-1"
                        data-testid="expires-hours-select"
                      >
                        <option value={6}>6 hours</option>
                        <option value={12}>12 hours</option>
                        <option value={24}>24 hours</option>
                        <option value={48}>48 hours</option>
                        <option value={72}>72 hours</option>
                      </select>
                      <span className="text-xs text-amber-400">Post disappears after this time</span>
                    </div>
                  )}
                </div>

                {/* PPV + Tier Controls */}
                <div className="flex items-center gap-4 flex-wrap">
                  <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                    <input type="checkbox" checked={newPost.is_ppv} onChange={e => setNewPost({ ...newPost, is_ppv: e.target.checked })} className="rounded" />
                    <DollarSign size={14} /> Pay-Per-View
                  </label>
                  {newPost.is_ppv && (
                    <Input type="number" min={1} value={newPost.ppv_price} onChange={e => setNewPost({ ...newPost, ppv_price: parseFloat(e.target.value) || 1 })} className="w-24 bg-zinc-800 border-zinc-700 text-white" placeholder="Price" />
                  )}
                  <select value={newPost.tier_required} onChange={e => setNewPost({ ...newPost, tier_required: e.target.value })} className="bg-zinc-800 border border-zinc-700 text-white text-sm px-3 py-2">
                    <option value="free">Free</option>
                    <option value="standard">Standard</option>
                    <option value="vip">VIP+</option>
                    <option value="diamond">Diamond</option>
                  </select>
                </div>
                <Button onClick={handleCreatePost} disabled={posting || uploading} className="w-full bg-gradient-to-r from-rose-500 to-pink-600 text-white font-bold" data-testid="dashboard-publish-btn">
                  {uploading ? 'Uploading media...' : posting ? 'Publishing...' : newPost.teaser_prompt ? 'Publish Post + Generate Teaser' : 'Publish Post'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Referral Tab */}
          <TabsContent value="referral">
            <div className="bg-zinc-900 border border-zinc-800 p-6">
              <h3 className="font-bold text-white mb-4 flex items-center gap-2"><Share2 size={18} className="text-emerald-400" /> Creator Referral Program</h3>
              {loadingRef ? (
                <div className="flex items-center justify-center py-8"><div className="w-6 h-6 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>
              ) : referral ? (
                <div className="space-y-5">
                  {/* Referral Link */}
                  <div className="bg-zinc-800 border border-zinc-700 p-4">
                    <p className="text-xs text-zinc-400 mb-2">Your unique referral link — share on social media, DMs, bio links:</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-white font-mono truncate" data-testid="referral-link-display">
                        {appUrl}/register?creator_ref={referral.referral_code}
                      </div>
                      <Button onClick={() => copyToClipboard(`${appUrl}/register?creator_ref=${referral.referral_code}`)} className="bg-emerald-500 text-white" data-testid="copy-referral-btn">
                        <Copy size={14} className="mr-1" /> Copy
                      </Button>
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-2">Code: <span className="font-mono text-white">{referral.referral_code}</span></p>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-zinc-800 border border-zinc-700 p-3 text-center">
                      <p className="text-xs text-zinc-500">Total Referrals</p>
                      <p className="font-bold text-2xl text-white" data-testid="referral-count">{referral.total_referrals}</p>
                    </div>
                    <div className="bg-zinc-800 border border-zinc-700 p-3 text-center">
                      <p className="text-xs text-zinc-500">Referral Earnings</p>
                      <p className="font-bold text-2xl text-green-400" data-testid="referral-earnings">{formatCurrency(referral.total_referral_earnings)}</p>
                    </div>
                    <div className="bg-zinc-800 border border-zinc-700 p-3 text-center">
                      <p className="text-xs text-zinc-500">Per Referral</p>
                      <p className="font-bold text-2xl text-amber-400">{formatCurrency(referral.bonus_creator)}</p>
                    </div>
                  </div>

                  {/* Bonus Settings */}
                  <div className="bg-zinc-800/50 border border-zinc-700 p-4">
                    <p className="text-sm font-semibold text-white mb-3">Referral Bonus Settings</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-zinc-400 mb-1 block">You earn per referral ($)</label>
                        <Input type="number" min={1} max={20} value={profileForm.referral_bonus_creator} onChange={e => setProfileForm({ ...profileForm, referral_bonus_creator: parseFloat(e.target.value) || 5 })} className="bg-zinc-900 border-zinc-700 text-white" data-testid="bonus-creator-input" />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-400 mb-1 block">New subscriber gets ($)</label>
                        <Input type="number" min={1} max={10} value={profileForm.referral_bonus_subscriber} onChange={e => setProfileForm({ ...profileForm, referral_bonus_subscriber: parseFloat(e.target.value) || 2 })} className="bg-zinc-900 border-zinc-700 text-white" data-testid="bonus-subscriber-input" />
                      </div>
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-2">Max: $20/referral for you, $10 for subscriber. Save via Edit Profile tab.</p>
                  </div>

                  {/* Recent Referrals */}
                  {referral.recent_referrals.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-white mb-2">Recent Referrals</p>
                      <div className="space-y-2">
                        {referral.recent_referrals.map((ref, i) => (
                          <div key={i} className="bg-zinc-800 border border-zinc-700 px-3 py-2 flex items-center justify-between text-sm">
                            <span className="text-zinc-400 text-xs">{new Date(ref.created_at).toLocaleDateString()}</span>
                            <span className="text-green-400 font-mono">+{formatCurrency(ref.creator_bonus)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm">Failed to load referral data. Try refreshing.</p>
              )}
            </div>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="bg-zinc-900 border border-zinc-800 p-6">
              <h3 className="font-bold text-white mb-4">Edit Creator Profile</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Display Name</label>
                  <Input value={profileForm.display_name} onChange={e => setProfileForm({ ...profileForm, display_name: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" data-testid="profile-display-name" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Bio</label>
                  <Textarea value={profileForm.bio} onChange={e => setProfileForm({ ...profileForm, bio: e.target.value })} rows={3} className="bg-zinc-800 border-zinc-700 text-white" data-testid="profile-bio" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Tags (comma separated)</label>
                  <Input value={profileForm.tags} onChange={e => setProfileForm({ ...profileForm, tags: e.target.value })} placeholder="fitness, lifestyle, cooking" className="bg-zinc-800 border-zinc-700 text-white" data-testid="profile-tags" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Standard $/mo</label>
                    <Input type="number" min={5} value={profileForm.tier_standard_price} onChange={e => setProfileForm({ ...profileForm, tier_standard_price: parseFloat(e.target.value) || 10 })} className="bg-zinc-800 border-zinc-700 text-white" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">VIP $/mo</label>
                    <Input type="number" min={10} value={profileForm.tier_vip_price} onChange={e => setProfileForm({ ...profileForm, tier_vip_price: parseFloat(e.target.value) || 25 })} className="bg-zinc-800 border-zinc-700 text-white" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Diamond $/mo</label>
                    <Input type="number" min={20} value={profileForm.tier_diamond_price} onChange={e => setProfileForm({ ...profileForm, tier_diamond_price: parseFloat(e.target.value) || 50 })} className="bg-zinc-800 border-zinc-700 text-white" />
                  </div>
                </div>
                <Button onClick={handleSaveProfile} disabled={savingProfile} className="w-full bg-white text-black font-bold" data-testid="save-profile-btn">
                  {savingProfile ? 'Saving...' : 'Save Profile'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* PPV Message Tab */}
          <TabsContent value="ppv">
            <div className="bg-zinc-900 border border-zinc-800 p-6">
              <h3 className="font-bold text-white mb-4 flex items-center gap-2"><Send size={16} className="text-amber-400" /> Send PPV Message</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Recipient User ID</label>
                  <Input value={ppvMsg.recipient_id} onChange={e => setPpvMsg({ ...ppvMsg, recipient_id: e.target.value })} placeholder="User ID" className="bg-zinc-800 border-zinc-700 text-white" data-testid="ppv-recipient" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Preview (shown before unlock)</label>
                  <Input value={ppvMsg.preview_text} onChange={e => setPpvMsg({ ...ppvMsg, preview_text: e.target.value })} placeholder="Exclusive content just for you..." className="bg-zinc-800 border-zinc-700 text-white" data-testid="ppv-preview" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Content (hidden until paid)</label>
                  <Textarea value={ppvMsg.content} onChange={e => setPpvMsg({ ...ppvMsg, content: e.target.value })} rows={3} className="bg-zinc-800 border-zinc-700 text-white" data-testid="ppv-content" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Price ($)</label>
                  <Input type="number" min={1} value={ppvMsg.price} onChange={e => setPpvMsg({ ...ppvMsg, price: parseFloat(e.target.value) || 1 })} className="w-32 bg-zinc-800 border-zinc-700 text-white" data-testid="ppv-price" />
                </div>
                <Button onClick={handleSendPPV} disabled={sendingPPV} className="w-full bg-amber-500 text-black font-bold" data-testid="send-ppv-btn">
                  {sendingPPV ? 'Sending...' : 'Send PPV Message'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Rooms Tab */}
          <TabsContent value="rooms">
            <div className="space-y-3">
              {analytics.rooms?.length === 0 ? (
                <div className="text-center py-12 bg-zinc-900 border border-zinc-800">
                  <Radio className="mx-auto mb-3 text-zinc-600" size={40} />
                  <p className="text-zinc-400">No rooms created yet</p>
                  <Link to="/creators"><Button className="mt-3 bg-rose-500 text-white">Browse Creator Hub</Button></Link>
                </div>
              ) : (
                analytics.rooms?.map(room => (
                  <Link key={room.id} to={`/creator/${room.id}`}>
                    <div className="bg-zinc-900 border border-zinc-800 p-4 hover:border-zinc-600 transition-colors flex items-center justify-between" data-testid={`dashboard-room-${room.id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 flex items-center justify-center ${room.is_live ? 'bg-red-500/20' : 'bg-violet-500/20'}`}>
                          {room.is_live ? <Zap size={18} className="text-red-400" /> : <Radio size={18} className="text-violet-400" />}
                        </div>
                        <div>
                          <h3 className="font-semibold text-white">{room.name}</h3>
                          <p className="text-xs text-zinc-500">{room.subscriber_count} subscribers &middot; {formatCurrency(room.price_per_month)}/mo</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-bold text-green-400">{formatCurrency(room.total_tips || 0)} tips</p>
                        {room.is_live && <span className="text-xs text-red-400 font-bold">LIVE</span>}
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
